import VueWrapper from '@/components/core/Vue/vue.wrapper';
import ClientFooterComponent from '@/components/vuetify/client-footer/client-footer.component';
import {PricingPlan} from '@/globals';
import {PlanApiService} from '@/sdk/api-services';
import {PaymentApiService} from '@/sdk/api-services/payment.api';
import {PlanModel} from '@/sdk/models/payment/payment.models';
import {UserSetupService} from '@/views/shared/services';
import {loadPaymentWidget, PaymentWidgetInstance} from '@tosspayments/payment-widget-sdk';
import {Component} from 'vue-property-decorator';

@Component({
    components: {
        ClientFooterComponent,
    },
})
export default class CheckoutComponent extends VueWrapper {
    private readonly widgetClientKey = process.env.VUE_APP_TOSS_WIDGET_CLIENT_KEY ?? 'live_gck_ALnQvDd2VJGaZjAAoNqwrMj7X41m';
    public readonly isLocalPaymentBypassEnabled = process.env.NODE_ENV !== 'production' && process.env.VUE_APP_PAYMENT_BYPASS === 'true';
    private customerKey: string = '';

    public isLoading: boolean = true;
    public isWidgetReady: boolean = false;
    private paymentWidget: PaymentWidgetInstance | null = null;

    public selectedPlan: PricingPlan | null = null;
    public hasCircuitModule: boolean = false;
    public extraUsersCount: number = 0;
    public selectedPlanId: string | null = null;

    public orderId: string = '';
    public orderName: string = '';
    public totalAmountKrw: number = 0;

    public exchangeRate: number | null = null;

    public userSetupSrv: UserSetupService = new UserSetupService();
    public paymentSrv: PaymentApiService = new PaymentApiService();
    public planApi: PlanApiService = new PlanApiService();

    public created() {
        this.customerKey = this.UserSessionSrv.accountId!;
        this.parseUrlParams();

        console.log(process.env);
    }

    public async mounted() {
        if (!this.selectedPlanId) {
            this.isLoading = false;
            this.$router.push({name: 'SubscriptionPlan'});
            return;
        }

        try {
            await this.loadExchangeRate();
            await this.loadSelectedPlan();
            if (!this.selectedPlan) {
                this.isLoading = false;
                this.$router.push({name: 'SubscriptionPlan'});
                return;
            }
            await this.initializeOrderOnBackend();

            this.isLoading = false;

            await this.$nextTick();

            await this.initializePaymentWidget();
        } catch (error) {
            console.error('Initialization failed', error);
            alert('Failed to initialize order. Please try again.');
            this.$router.push({name: 'SubscriptionPlan'});
            this.isLoading = false;
        }
    }

    private async loadExchangeRate() {
        const data = await this.paymentSrv.getExchangeRate();
        const rate = data?.rate;

        if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) {
            throw new Error('Invalid exchange rate response');
        }

        this.exchangeRate = Math.round(rate);
    }

    private parseUrlParams() {
        const query = this.$route.query;
        const planId = query.planId as string;
        this.hasCircuitModule = query.includeCircuit === 'true';
        this.extraUsersCount = parseInt(query.extraUsers as string, 10) || 0;
        this.selectedPlanId = planId ?? null;
    }

    private async loadSelectedPlan() {
        if (!this.selectedPlanId) return;

        const accountId = this.UserSessionSrv.Session?.selectedAccountId ?? this.UserSessionSrv.Session?.accountId ?? null;

        if (accountId) {
            const availablePlans = await this.planApi.getAvailablePlans(accountId);
            const selectedPlan = availablePlans.find(p => p.id === this.selectedPlanId);
            if (selectedPlan) {
                this.selectedPlan = this.mapPlan(selectedPlan);
                return;
            }
        }

        const publicPlan = await this.planApi.getPublicPlanById(this.selectedPlanId);
        this.selectedPlan = this.mapPlan(publicPlan);
    }

    private mapPlan(plan: PlanModel): PricingPlan {
        const settings = plan.settings ?? {};
        return {
            id: plan.id,
            name: plan.name,
            equipmentLimit: settings.limits?.maxLocations ?? 0,
            includedUsers: settings.limits?.maxUsers ?? 0,
            priceUsd: plan.priceUsd,
            periodDays: plan.periodDays ?? 30,
            sortOrder: plan.sortOrder ?? 0,
            additionalUserPriceUsd: settings.pricing?.additionalUserPriceUsd ?? 0,
            circuitModulePriceUsd: settings.addons?.electricCircuitModule?.priceUsd ?? 0,
        };
    }

    private async initializeOrderOnBackend() {
        if (!this.selectedPlan) return;

        const request = {
            userId: this.UserSessionSrv.userId!,
            planId: this.selectedPlan.id,
            includeCircuit: this.hasCircuitModule,
            extraUsers: this.extraUsersCount,
        };

        const paymentRecord = await this.paymentSrv.initPayment(request);

        this.orderId = paymentRecord.orderId;
        this.totalAmountKrw = Math.round(paymentRecord.amount);
        const backendExchangeRate = paymentRecord.pricingSnapshot?.exchangeRate;
        if (typeof backendExchangeRate === 'number' && Number.isFinite(backendExchangeRate) && backendExchangeRate > 0) {
            this.exchangeRate = Math.round(backendExchangeRate);
        }
        this.orderName = paymentRecord.orderName;
    }

    private async initializePaymentWidget() {
        try {
            this.paymentWidget = await loadPaymentWidget(this.widgetClientKey, this.customerKey);

            await this.paymentWidget.renderPaymentMethods('#payment-widget', {value: this.totalAmountKrw}, {variantKey: 'DEFAULT'});

            await this.paymentWidget.renderAgreement('#payment-agreement');

            this.isWidgetReady = true;
        } catch (error) {
            console.error('Failed to initialize Toss Payment Widget:', error);
            alert(this.$t('PaymentPage.Checkout.alertWidgetLoadFailed'));
        }
    }

    public async requestPayment() {
        if (!this.paymentWidget || !this.isWidgetReady) {
            alert(this.$t('PaymentPage.Checkout.alertWidgetNotReady'));
            return;
        }

        const currentQuery = new URLSearchParams(this.$route.query as Record<string, string>);

        const successUrl = `${window.location.origin}/payment/success?${currentQuery.toString()}`;
        const failUrl = `${window.location.origin}/payment/fail?${currentQuery.toString()}`;

        console.log('Requesting payment with:', {
            orderId: this.orderId,
            orderName: this.orderName,
            successUrl: successUrl,
            failUrl: failUrl,
        });

        try {
            await this.paymentWidget.requestPayment({
                orderId: this.orderId,
                orderName: this.orderName,
                successUrl: successUrl,
                failUrl: failUrl,
                customerEmail: this.UserSessionSrv.Session?.email || undefined,
                customerName: this.UserSessionSrv.Session?.FirstName + ' ' + this.UserSessionSrv.Session?.LastName || undefined,
            });
        } catch (error) {
            console.error('Payment request failed:', error);
            alert(this.$t('PaymentPage.Checkout.alertPaymentFailed', {error: error}));
        }
    }

    get planPriceKrw(): number {
        if (!this.selectedPlan) return 0;
        return this.convertUsdToKrw(this.selectedPlan.priceUsd);
    }

    get circuitPriceKrw(): number {
        const priceUsd = this.selectedPlan?.circuitModulePriceUsd ?? 0;
        return this.hasCircuitModule ? this.convertUsdToKrw(priceUsd) : 0;
    }

    get extraUsersPriceKrw(): number {
        const priceUsd = this.selectedPlan?.additionalUserPriceUsd ?? 0;
        return this.convertUsdToKrw(this.extraUsersCount * priceUsd);
    }

    private convertUsdToKrw(valueUsd: number): number {
        if (this.exchangeRate === null) {
            throw new Error('Exchange rate is not loaded');
        }

        const converted = valueUsd * this.exchangeRate;
        return Math.round(converted);
    }

    get exchangeRateDisplay(): string {
        return this.exchangeRate === null ? '-' : this.exchangeRate.toLocaleString();
    }

    public formatPrice(value: number, currency: 'KRW' = 'KRW'): string {
        return new Intl.NumberFormat('ko-KR', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }

    public skipPaymentForLocalTesting() {
        if (!this.isLocalPaymentBypassEnabled || !this.selectedPlan || !this.orderId || !this.totalAmountKrw) {
            return;
        }

        this.$router.push({
            name: 'Payment Success',
            query: {
                paymentKey: 'LOCAL_BYPASS',
                orderId: this.orderId,
                amount: String(this.totalAmountKrw),
                planId: this.selectedPlan.id,
            },
        });
    }
}
